import React, { useState } from "react";
import { Container, Button, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useFadeIn } from "../App";
import ShapeDivider from "../components/ShapeDivider";
import SafeVideo from "../components/SafeVideo";
import ImageModal from "../components/ImageModal";
import { pageImageAllocations, getImageById } from "../data/wildlifeGallery";

// Define PayPal link as a constant for easy updates
const paypalLink =
  "https://www.paypal.com/donate/?hosted_button_id=JWCTWZHY89WV2";

const Home = () => {
  const [introRef, introVisible] = useFadeIn();
  const [pillarsRef, pillarsVisible] = useFadeIn();
  const [ctaRef, ctaVisible] = useFadeIn();
  const [successRef, successVisible] = useFadeIn();
  const [videosRef, videosVisible] = useFadeIn();
  const [testimonialRef, testimonialVisible] = useFadeIn();

  // Modal state for image viewing
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState({ url: '', alt: '' });

  const openImageModal = (imageUrl, imageAlt) => {
    setModalImage({ url: imageUrl, alt: imageAlt });
    setShowModal(true);
  };

  return (
    <>
      {/* Premium Hero Section */}
      <header className="premium-hero-section">
        {/* Background Video/Image */}
        <div className="hero-background">
          <img
            src="https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F21a232269eb04c018ef1e56eff14c55b?format=webp&width=2560&quality=95"
            alt="Community wildlife education event with eagle costume - engaging and educating the public about wildlife conservation"
            className="hero-background-image"
            loading="eager"
          />
          <div className="hero-gradient-overlay"></div>
          <div className="hero-particles"></div>
        </div>

        {/* Hero Content */}
        <Container className="hero-container">
          <Row className="h-100 align-items-center justify-content-center">
            <Col lg={12} xl={11} className="text-center hero-content-column">
              {/* Premium Badge */}
              <div className="hero-badge mb-4">
                <span className="badge-text">
                  <i className="fas fa-leaf me-2"></i>
                  Australia's Premier Wildlife Conservation
                </span>
              </div>

              {/* Main Heading */}
              <h1 className="hero-title">
                <span className="hero-title-line">We Nurture</span>
                <span className="hero-title-line hero-title-accent">
                  Nature
                </span>
              </h1>

              {/* Subtitle */}
              <p className="hero-subtitle">
                Ethical rehabilitation and sustainable care for Australia's
                unique and precious wildlife through innovative conservation
                practices
              </p>

              {/* Statistics */}
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-number">10,000+</div>
                  <div className="stat-label">animals rehabilitated</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-number">300+</div>
                  <div className="stat-label">Rescues</div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Release Sites</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="hero-actions">
                <Button
                  href={paypalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-premium-primary"
                  size="lg"
                >
                  <i className="fas fa-heart me-2"></i>
                  Support Our Mission
                </Button>
                <Button
                  as={Link}
                  to="/about"
                  className="btn-premium-secondary"
                  size="lg"
                >
                  <span>Discover Our Story</span>
                  <i className="fas fa-arrow-right ms-2"></i>
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="hero-trust-badges">
                <div className="trust-item">
                  <i className="fas fa-award"></i>
                  <span>Licenced & Certified</span>
                </div>
                <div className="trust-item">
                  <i className="fas fa-shield-alt"></i>
                  <span>Ethical Practices</span>
                </div>
                <div className="trust-item">
                  <i className="fas fa-users"></i>
                  <span>Community Trusted</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </header>

      {/* Intro & About */}
      <section
        ref={introRef}
        className={`fade-in-section ${introVisible ? "is-visible" : ""}`}
      >
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <h2 className="mb-4">Our Story</h2>
              <p className="lead">
                Aussie Wildlife Limited was born from a fierce determination to
                provide sustainable care for Australia's wildlife.
              </p>
              <p>
                Our founder, an experienced carer, recognised the need for
                long-term solutions in wildlife care and created the
                organisation to promote ethical and effective conservation
                practices. With our skilled and diverse team, we develop
                innovative strategies for promoting sustainable and ethical
                practices in wildlife care and conservation.
              </p>
              <p>
                We inspire ecological regeneration through regenerative
                storytelling and community-led action. Our vision is to create a
                world where all living beings thrive, and humans are part of a
                healthy and diverse ecosystem.
              </p>
            </Col>
            <Col lg={6}>
              <div className="ratio ratio-16x9 rounded-3 overflow-hidden shadow-lg">
                <iframe
                  src="https://player.vimeo.com/video/1097608296?title=0&byline=0&portrait=0&badge=0&autopause=0&controls=1&autoplay=0&muted=0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                  title="Aussie Wildlife Limited Story"
                  className="rounded-3"
                  style={{ border: "none" }}
                ></iframe>
              </div>
            </Col>
          </Row>
        </Container>
        <ShapeDivider />
      </section>

      {/* 4 Pillars Section */}
      <section
        ref={pillarsRef}
        className={`section-bg fade-in-section ${
          pillarsVisible ? "is-visible" : ""
        }`}
      >
        <Container className="text-center">
          <h2 className="mb-4">Our Holistic Approach</h2>
          <p className="lead mb-5 mx-auto" style={{ maxWidth: "700px" }}>
            Our 4 Pillars of Sustainable Care ensure every animal receives the
            best possible chance of a successful return to the wild.
          </p>
          <Row className="g-4">
            {["Found a bird?", "Our Model", "Our Team", "Care Map"].map(
              (pillar, index) => {
                const descriptions = [
                  // Found a bird?
                  <div className="text-start">
                    <ul className="list-unstyled text-muted mb-0">
                      <li className="mb-2">
                        • Found an injured bird and don't know what to do?
                      </li>
                      <li className="mb-2">
                        �� Curious to know about different bird types?
                      </li>
                      <li className="mb-0">• Curious about bird diets?</li>
                    </ul>
                  </div>,
                  // Our Model
                  <div className="text-start">
                    <p className="text-muted mb-2">
                      Our sustainable and ethical rehabilitation model
                      prioritises Care, Environment, Natural foods, and our
                      Buddies.
                    </p>
                    <p className="mb-0">
                      If you're interested to know more,{" "}
                      <Link
                        to="/what-we-do"
                        className="text-decoration-none fw-semibold"
                      >
                        click here to find out!
                      </Link>
                    </p>
                  </div>,
                  // Our Team
                  <div className="text-start">
                    <p className="text-muted mb-2">
                      Interested in getting to know and interacting with the
                      staff who are working tirelessly behind the scenes?
                    </p>
                    <p className="text-muted mb-3">
                      Or even if you're just a bit nosy!
                    </p>
                    <Button
                      as={Link}
                      to="/team"
                      variant="outline-primary"
                      size="sm"
                    >
                      Meet Our Team
                    </Button>
                  </div>,
                  // Care Map
                  <div className="text-start">
                    <p className="text-muted mb-2">
                      If you would like to see the many many satisfied customers
                      we've helped.......look no further!
                    </p>
                    <p className="text-muted mb-3">
                      We've got a whole map of them!
                    </p>
                    <Button
                      as={Link}
                      to="/care-map"
                      variant="outline-primary"
                      size="sm"
                    >
                      View Care Map
                    </Button>
                  </div>,
                ];

                const icons = [
                  "fa-dove",
                  "fa-heart-pulse",
                  "fa-users",
                  "fa-map-marked-alt",
                ];

                return (
                  <Col md={6} lg={6} xl={3} key={pillar}>
                    <div className="h-100 p-4 border rounded-3 shadow-sm bg-white">
                      <div className="text-center mb-3">
                        <div
                          className="icon-circle-small mb-3"
                          style={{
                            backgroundColor: "var(--primary-yellow)",
                            color: "white",
                          }}
                        >
                          <i className={`fas ${icons[index]}`}></i>
                        </div>
                        <h4 className="fw-bold mb-3">{pillar}</h4>
                      </div>
                      {descriptions[index]}
                    </div>
                  </Col>
                );
              },
            )}
          </Row>
        </Container>
      </section>

      {/* How You Can Help (CTA) */}
      <section
        ref={ctaRef}
        className={`cta-section fade-in-section ${
          ctaVisible ? "is-visible" : ""
        }`}
      >
        <Container className="text-center">
          <h2 className="mb-4">You Can Make a Difference</h2>
          <p className="lead mb-5 mx-auto" style={{ maxWidth: "700px" }}>
            Our work is only possible through the generous support of our
            community. Join us in protecting Australia's wildlife.
          </p>
          <Row className="g-4">
            <Col lg={6}>
              <div className="cta-card text-center">
                <i className="fas fa-hand-holding-heart fa-3x mb-3"></i>
                <h3 className="text-white">Donate</h3>
                <p>
                  Your contribution directly funds food, medicine, and housing
                  for animals in our care. Every dollar helps.
                </p>
                {/* UPDATED: "Give Today" button now links to PayPal */}
                <Button
                  href={paypalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="secondary"
                  size="lg"
                >
                  Give Today
                </Button>
              </div>
            </Col>
            <Col lg={6}>
              <div className="cta-card text-center">
                <i className="fas fa-hands-helping fa-3x mb-3"></i>
                <h3 className="text-white">Volunteer</h3>
                <p>
                  Become a hands-on carer, help with transport, or assist with
                  community events. We need your skills and passion.
                </p>
                <Button
                  as={Link}
                  to="/contact"
                  variant="outline-light"
                  size="lg"
                >
                  Join Our Team
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
        <ShapeDivider top color="#F9F7F2" />
      </section>

      {/* Wildlife Gallery Showcase */}
      <section
        ref={successRef}
        className={`fade-in-section ${successVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Our Wildlife in Action
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Every day brings new rescue stories and rehabilitation successes.
                See the beautiful wildlife we care for across Queensland.
              </p>
            </Col>
          </Row>
          <Row className="g-4 mb-5">
            {pageImageAllocations.home.gallery.map((imageId, index) => {
              const image = getImageById(imageId);
              return (
                <Col md={6} lg={4} key={image.id}>
                  <div
                    className="wildlife-gallery-card shadow-lg"
                    style={{
                      borderRadius: "20px",
                      overflow: "hidden",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    }}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="w-100"
                      style={{
                        height: "250px",
                        objectFit: image.id === 'tawny-frogmouth-new' ? "contain" : "cover",
                        backgroundColor: image.id === 'tawny-frogmouth-new' ? "#f8f9fa" : "transparent",
                        transition: "transform 0.3s ease",
                        cursor: "pointer"
                      }}
                      onClick={() => openImageModal(image.url, image.alt)}
                    />
                  </div>
                </Col>
              );
            })}
            {[
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Faf53d58b2b45469794a2e58a8808e802?format=webp&width=800",
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fbe36bb1801c646e196984127b331e2af?format=webp&width=800",
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F3fabbce7fcb343d49cc8e2667f7dabb0?format=webp&width=800",
              "https://cdn.builder.io/api/v1/image/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fe7dee9f0deb142a5a4372081b599db6e?format=webp&width=800"
            ].map((imageUrl, index) => (
              <Col md={6} lg={4} key={`new-image-${index}`}>
                <div
                  className="wildlife-gallery-card shadow-lg"
                  style={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="Wildlife education and conservation in action"
                    className="w-100"
                    style={{
                      height: "250px",
                      objectFit: "cover",
                      objectPosition: "top center",
                      transition: "transform 0.3s ease",
                      cursor: "pointer"
                    }}
                    onClick={() => openImageModal(imageUrl, "Wildlife education and conservation in action")}
                  />
                </div>
              </Col>
            ))}
          </Row>

          {/* Success Story Feature */}
          <Row className="align-items-center g-5 mt-5">
            <Col lg={6}>
              <img
                src={getImageById(pageImageAllocations.home.successStory).url}
                className="img-fluid rounded-3 shadow-lg rehabilitation-success-image"
                alt={getImageById(pageImageAllocations.home.successStory).alt}
                style={{
                  width: "100%",
                  objectFit: "cover",
                  objectPosition: "center top"
                }}
              />
            </Col>
            <Col lg={6}>
              <div
                className="success-badge mb-3"
                style={{
                  display: "inline-block",
                  background: "var(--primary-yellow)",
                  color: "white",
                  padding: "8px 20px",
                  borderRadius: "50px",
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                <i className="fas fa-star me-2"></i>
                Success Story
              </div>
              <h2 className="mb-3" style={{ color: "var(--primary-green)" }}>Our Rehabilitation Success</h2>
              <p className="text-muted mb-4" style={{ fontSize: "1.1rem", lineHeight: "1.7" }}>
                Every animal that comes through our doors has a unique story. From emergency rescues
                to long-term rehabilitation, we provide specialised care tailored to each species' needs.
              </p>
              <div className="success-stats mb-4">
                <Row>
                  <Col md={6} className="mb-3">
                    <div className="stat-item text-center p-3 rounded-3" style={{ background: "var(--light-bg)" }}>
                      <h4 className="fw-bold mb-1" style={{ color: "var(--primary-green)" }}>10,000+</h4>
                      <small className="text-muted">Animals Rehabilitated</small>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="stat-item text-center p-3 rounded-3" style={{ background: "var(--light-bg)" }}>
                      <h4 className="fw-bold mb-1" style={{ color: "var(--primary-green)" }}>120+</h4>
                      <small className="text-muted">Mammals Rescued</small>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
        <ShapeDivider />
      </section>

      {/* Behind the Scenes Videos */}
      <section
        ref={videosRef}
        className={`fade-in-section ${videosVisible ? "is-visible" : ""}`}
        style={{ padding: "100px 0", background: "white" }}
      >
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2
                className="mb-4"
                style={{ color: "var(--primary-green)", fontSize: "2.5rem" }}
              >
                Behind the Scenes
              </h2>
              <p
                className="lead"
                style={{ fontSize: "1.2rem", color: "var(--dark-text)" }}
              >
                Experience the daily moments of wildlife rehabilitation - from rescue to recovery,
                every step brings hope and healing to Australia's precious native animals
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            {[
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Fa3416c2790514829bcc0dc21fde88cd3?alt=media&token=0e2e019a-2c2d-493d-8a39-ac6036be25d6&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2Ff4c042bfb8cd4e12b88d8ffa6c699060?alt=media&token=8b1c2ebc-8e51-416d-9cbf-ba68d6418d9d&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F28eeb44dcb5e488ea9b7684fd889498b?alt=media&token=bbe267ff-78e1-4548-9ffd-7674a2e46e21&apiKey=35ffb80beae24a4483ace4ab1c92b206",
              "https://cdn.builder.io/o/assets%2F35ffb80beae24a4483ace4ab1c92b206%2F9bf78e3f91584aeca2fbebc6878d4ee7?alt=media&token=140df37c-366c-4c99-9870-507f81ac3a56&apiKey=35ffb80beae24a4483ace4ab1c92b206"
            ].map((videoUrl, index) => (
              <Col md={6} lg={3} key={index}>
                <div
                  className="video-container shadow-lg"
                  style={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  }}
                >
                  <SafeVideo
                    src={videoUrl}
                    controls
                    className="w-100"
                    style={{ aspectRatio: "3/4", objectFit: "cover" }}
                    preload="metadata"
                    autoGeneratePoster={true}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Testimonial */}
      <section
        ref={testimonialRef}
        className={`section-bg fade-in-section ${
          testimonialVisible ? "is-visible" : ""
        }`}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <div className="testimonial-card">
                <p className="testimonial-quote">
                  "Volunteering with Aussie Wildlife has been the most rewarding
                  experience of my life. Seeing an animal you've cared for
                  return to the wild is a feeling you can't describe. The
                  support and training they provide is second to none."
                </p>
                <p className="testimonial-author">
                  — Sarah J., Volunteer Carer
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Image Modal */}
      <ImageModal
        show={showModal}
        onHide={() => setShowModal(false)}
        imageUrl={modalImage.url}
        imageAlt={modalImage.alt}
      />
    </>
  );
};

export default Home;
